import {
    Gender,
    Nationality,
    type PrevPax,
    type SavedItinerary,
    type SubmitBuyerPassenger,
} from './types';
import { getCookie } from '@store/auth/types';
import {
    type AddBillSuggestedServiceRequest,
    AnalyticsApiApiAnalyticsGoogleAnalyticsBillCodePost,
    AnalyticsApiApiAnalyticsRadar361BillCodePost,
    AnalyticsApiApiAnalyticsSafarmarketBillCodePost,
    AnalyticsApiApiAnalyticsTakhfifanBillCodePost,
    AnalyticsApiApiAnalyticsWebengageBillCodePost,
    BillsApiApiBillsSuggestedServiceBillCodePost,
    PreviousPaxApiApiPreviousPaxDefaultPassengerGet,
    PreviousPaxApiApiPreviousPaxPlayerIdGet,
    PreviousPaxOrderByType,
    type TravelersHistoryResponse,
} from '@clients/BillApi';
import { type ExtraField } from '@clients/FlightApi';
import ItineraryPackage from '@itinerary/ItineraryPackage';
import type Trip from '@itinerary/trip/Trip';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import {
    DiscountType,
    type ItineraryPackageInvoiceEntries,
} from '@store/invoice';
import ItineraryModule from '@itinerary/ItineraryModule';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { ReserveType } from '@utils/itinerary';
import { type Utm } from '@store/auth';
import type PassengerForm from '@itinerary/PassengerForm';
import {
    ItineraryVerifyException,
    VerificationStatus,
} from '@itinerary/Exceptions';
import type VerifyResult from '@itinerary/VerifyResult';
import { PaxType } from '@utils/types';
import { type PassengerCount } from '@itinerary/types';
import type SuggestionService from '@itinerary/trip/SuggestionService';

export class FormStore {
    //#region state
    /**
     * disable validate pax types
     *
     * @type {boolean}
     * @memberof FormStore
     */
    disablePaxTypeValidation = false;
    emptyPackage: ItineraryPackage | null = null;

    /**
     * Indicates the Status of verification (either loading, verified, unknown Error, is full, unable to connect, cancelled or none)
     *
     * @type {VerificationStatus}
     * @memberof FormStore
     */
    verificationStatus = VerificationStatus.None;

    /**
     * Expiration time of form
     *
     * @type {Date | null}
     * @memberof FormStore
     */
    expiryTime: Date | null = null;

    /**
     * Subtitle of the form
     *
     * @type {string}
     * @memberof FormStore
     */
    formSubtitle =
        'لطفا نام و نام خانوادگی مسافر را مطابق کارت ملی، شناسنامه یا گذرنامه وارد نمایید.';

    /**
     * Map of itinerary package to cancellation terms and conditions on verify package
     *
     * @type {Map<ItineraryPackage, string>}
     * @memberof FormStore
     */
    packagesCancellationTerms: Map<ItineraryPackage, string> = new Map();

    /**
     * Number of adult passengers selected in form
     *
     * @memberof FormStore
     */
    adultCount = 0;

    /**
     * Number of child passengers selected in form
     *
     * @memberof FormStore
     */
    childCount = 0;

    /**
     * Number of infant passengers selected in form
     *
     * @memberof FormStore
     */
    infantCount = 0;

    adultForm: PassengerForm | null = null;

    childForm: PassengerForm | null = null;

    infantForm: PassengerForm | null = null;

    maxCapacity = 0;

    /**
     * Customized title for passenger of trip
     *
     * @type {string | null}
     * @memberof FormStore
     */
    customPassengerTitle: string | null = null;

    /**
     * Indicates whether the form needs a secondary mobile number, or not
     *
     * @type {boolean}
     * @memberof FormStore
     */
    needsDestinationMobile = false;

    /**
     * Information of passengers which user previously bought ticket for them
     *
     * @type {PrevPax[] | null}
     * @memberof FormStore
     */
    prevPax: PrevPax[] | null = null;

    /**
     * Loading status of information of previous passengers
     *
     * @type {LoadingStatus}
     * @memberof FormStore
     */
    prevPaxLoadingStatus = LoadingStatus.None;

    /**
     * Default passenger of trip in the form
     *
     * @type {PrevPax | null}
     * @memberof FormStore
     */
    defaultPassenger: SubmitBuyerPassenger | null = null;

    customErrorMessage: string | null = null;

    /**
     * Trip packages that is selected to be purchased
     *
     * @type {(ItineraryPackage[] | null)}
     * @memberof FormStore
     */
    itineraryPackages: ItineraryPackage[] = [];

    /**
     * For check to if load itinerary package from session not save it again
     *
     * @type {boolean}
     * @memberof FormStore
     */
    isLoadFromSession = false;

    reserveNotes: string[] = [];

    /**
     * Indicates whether the bus seats must be hidden
     *
     * @type {boolean}
     * @memberof FormStore
     */
    forceHideBusSeats = false;

    /**
     * Indicates whether the reserveType header be short
     *
     * @type {boolean}
     * @memberof FormStore
     */
    shortReserveHeaderEnabled = false;

    /**
     * The departure time that is seen by the user before making the reservation
     *
     * @type {(Date | null)}
     * @memberof FormStore
     */
    seenDepartureTime: Date | null = null;
    paxForms: PassengerForm[] = [];
    previousPaxOrderBy: PreviousPaxOrderByType =
        PreviousPaxOrderByType.UsageCount;

    formDirty = false;
    extraInfoFields: ExtraField[] = [];
    //#endregion

    get eventMetric() {
        const selectedSuggestionServices = this.itineraryPackages.reduce(
            (prev, current) => {
                if (current.isTripPackage()) {
                    return [
                        ...prev,
                        ...current.selectedSuggestionServices.map(s => ({
                            service: s.service,
                            SuggestedServiceId: s.service.id,
                            Count: s.count,
                            ArrivalTime: current.arrivalDateTime.toISOString(),
                            DepartureTime:
                                current.departureDateTime.toISOString(),
                            Description: s.description,
                        })),
                    ];
                }
                return prev;
            },
            [] as (AddBillSuggestedServiceRequest & {
                service: SuggestionService;
            })[]
        );
        return {
            raiseCount: selectedSuggestionServices.reduce(
                (prev, next) => prev + (next.Count || 0),
                0
            ),
            totalRaisePrice: selectedSuggestionServices.reduce(
                (prev, next) =>
                    prev + (next.Count || 0) * (next.service.price || 0),
                0
            ),
            raiseTitles: selectedSuggestionServices
                .map(({ service }) => service.title)
                .join('-'),
        };
    }

    /**
     * Sets the form configurations
     *
     * @memberof FormStore
     * @param verifyResult
     */
    setFormConfigs(verifyResult: VerifyResult) {
        this.disablePaxTypeValidation = verifyResult.disablePaxTypeValidation;
        this.adultForm = verifyResult.adultForm;
        this.childForm = verifyResult.childForm;
        this.infantForm = verifyResult.infantForm;
        this.paxForms = verifyResult.paxList.slice().sort((a, b) => {
            if (a.type === b.type) {
                return 0;
            }
            if (a.type === PaxType.Adult) {
                return -1;
            }
            if (b.type === PaxType.Adult) {
                return 1;
            }
            if (a.type === PaxType.Child) {
                return -1;
            }
            if (b.type === PaxType.Child) {
                return 1;
            }
            return 0;
        });
    }

    /**
     * Clears the verification status, expiration time, form configurations, cancellation terms and actual price
     *
     * @memberof FormStore
     */
    clearState() {
        this.verificationStatus = VerificationStatus.None;
        this.expiryTime = null;
        this.paxForms = [];
        this.adultForm = null;
        this.childForm = null;
        this.infantForm = null;
        this.maxCapacity = 0;
        this.packagesCancellationTerms = new Map();
        useBusSeatHandler().reset();
    }

    /**
     * Sets the number of passengers of each type
     *
     * @param {{number,number,number}} params
     * @memberof FormStore
     */
    setCounts(params: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }) {
        this.adultCount = params.adultCount;
        this.childCount = params.childCount;
        this.infantCount = params.infantCount;
    }

    /**
     * Sets the information of previous passengers
     *
     * @memberof FormStore
     * @param params
     */
    setPrevPax(params: TravelersHistoryResponse[]) {
        this.prevPax = params.map(p => {
            let paxType = PaxType.Adult;
            if (p.PaxType) {
                if (p.PaxType === 'INF') {
                    paxType = PaxType.Infant;
                } else if (p.PaxType === 'CHD') {
                    paxType = PaxType.Child;
                }
            }

            return {
                id: p.Id ?? 0,
                firstName: p.FirstName ?? '',
                lastName: p.LastName ?? '',
                persianFirstName: p.PersianFirstName ?? '',
                persianLastName: p.PersianLastName ?? '',
                birthDate: p.BirthDay
                    ? dayConfig.gregory(p.BirthDay).startOf('day').toDate()
                    : null,
                paxType,
                nationalCode:
                    p.NationalCode === '0' ? '' : p.NationalCode ?? '',
                passportNumber: p.PassportNumber ?? '',
                passportExpiry: p.PassportExpiration
                    ? dayConfig
                          .gregory(p.PassportExpiration)
                          .startOf('day')
                          .toDate()
                    : null,
                gender: p.Male ? Gender.Male : Gender.Female,
                nationality: p.NationalCode
                    ? Nationality.Iranian
                    : Nationality.Foreigner,
                nationalityCode: p.Nationality ?? null,
            };
        });
    }

    setActualDepartureTime(params: { index: number; time: Date }) {
        const tripPackages = this.itineraryPackages as TripPackage[];
        if (!tripPackages || !tripPackages[params.index]) {
            return;
        }
        if (tripPackages[params.index].segments[0]?.trips[0]) {
            const lastDepartureDateTime =
                tripPackages[params.index].segments[0].trips[0]
                    .departureDateTime;
            const lastArrivalDateTime =
                tripPackages[params.index].segments[0].trips[0].arrivalDateTime;
            const newArrivalDateTime = dayConfig
                .gregory(params.time)
                .add(
                    dayConfig
                        .gregory(lastArrivalDateTime)
                        .diff(lastDepartureDateTime, 'second'),
                    'second'
                );
            tripPackages[params.index].segments[0].trips[0].departureDateTime =
                params.time;
            tripPackages[params.index].segments[0].trips[0].departureTime =
                `${params.time
                    .getHours()
                    .toString()
                    .padStart(2, '0')}:${params.time
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}`;
            tripPackages[params.index].segments[0].trips[0].arrivalDateTime =
                newArrivalDateTime.toDate();
            tripPackages[params.index].segments[0].trips[0].arrivalTime =
                newArrivalDateTime.format('HH:mm');
        }
    }

    /**
     * GETTER - Shows the countdown timer of form expiry time
     *
     * @memberof FormStore
     */
    get showTimer() {
        return this.expiryTime !== null;
    }

    setItineraryPackagesAndDepartureTime(packages: ItineraryPackage[]) {
        this.itineraryPackages = packages;
        const [firstPackage] = packages;
        if (firstPackage?.isTripPackage()) {
            this.seenDepartureTime = new Date(firstPackage.departureDateTime);
        }
    }

    private get paxCountSummary(): PassengerCount {
        return this.paxForms.reduce(
            (prev, curr) => {
                switch (curr.type) {
                    case PaxType.Adult:
                        return {
                            ...prev,
                            adultCount: prev.adultCount + 1,
                        };
                    case PaxType.Child:
                        return {
                            ...prev,
                            childCount: prev.childCount + 1,
                        };
                    case PaxType.Infant:
                        return {
                            ...prev,
                            infantCount: prev.infantCount + 1,
                        };
                    default:
                        return prev;
                }
            },
            { infantCount: 0, adultCount: 0, childCount: 0 }
        );
    }

    deletePax(index: number) {
        const paxType = this.paxForms[index]?.type;
        const summary = this.paxCountSummary;
        switch (paxType) {
            case PaxType.Adult:
                if (summary.adultCount === 1) {
                    // This should never happen.
                    throw new MrError(
                        `Pax with pax type ${PaxType.Adult} cannot become empty.`,
                        true
                    );
                }
                if (summary.infantCount > summary.adultCount - 1) {
                    throw new MrError(
                        'تعداد نوزادان نمی‌تواند از تعداد بزرگسالان بیشتر باشد.',
                        true
                    );
                }
                if (
                    this.itineraryPackages.some(
                        pkg =>
                            pkg.minPersons ===
                            summary.childCount + summary.adultCount
                    )
                ) {
                    throw new MrError(
                        `حداقل تعداد مسافران برای این بلیط ${this.itineraryPackages[0]?.minPersons} مسافر می‌باشد.`,
                        true
                    );
                }
                this.adultCount--;
                break;
            case PaxType.Child:
                if (
                    this.itineraryPackages.some(
                        pkg =>
                            pkg.minPersons ===
                            summary.childCount + summary.adultCount
                    )
                ) {
                    throw new MrError(
                        `حداقل تعداد مسافران برای این بلیط ${this.itineraryPackages[0]?.minPersons} مسافر می‌باشد.`,
                        true
                    );
                }
                this.childCount--;
                break;
            case PaxType.Infant:
                this.infantCount--;
                break;
            default:
                // this should never happen
                throw new MrError(
                    `Removing pax type ${paxType} is not allowed.`
                );
        }
        this.paxForms = this.paxForms.filter((_, i) => i !== index);
        this.formDirty = true;
    }

    addPax(type: PaxType) {
        const summary = this.paxCountSummary;
        const seats = summary.childCount + summary.adultCount;
        switch (type) {
            case PaxType.Adult:
                if (this.maxCapacity === seats) {
                    throw new MrError(
                        'تعداد مسافران از حداکثر ظرفیت بیشتر است.',
                        true
                    );
                }
                if (this.adultForm === null) {
                    throw new Error('امکان افزایش تعداد مسافر وجود ندارد.');
                }
                this.formDirty = true;
                this.adultCount++;
                this.paxForms = [...this.paxForms, this.adultForm.clone()];
                break;
            case PaxType.Child:
                if (this.maxCapacity === seats) {
                    throw new MrError(
                        'تعداد مسافران از حداکثر ظرفیت بیشتر است.',
                        true
                    );
                }
                if (this.childForm === null) {
                    throw new Error('امکان افزایش تعداد مسافر وجود ندارد.');
                }
                this.formDirty = true;
                this.childCount++;
                this.paxForms = [...this.paxForms, this.childForm.clone()];
                break;
            case PaxType.Infant:
                if (summary.infantCount + 1 > summary.adultCount) {
                    throw new MrError(
                        'تعداد نوزادان نمی‌تواند از تعداد بزرگسالان بیشتر باشد.',
                        true
                    );
                }
                if (this.adultForm === null) {
                    throw new Error('امکان افزایش تعداد مسافر وجود ندارد.');
                }
                this.formDirty = true;
                this.infantCount++;
                this.paxForms = [...this.paxForms, this.infantForm!.clone()];
                break;
        }
    }

    /**
     * GETTER - Sum of price that was shown to the user earlier, to be checked with the final price
     *
     * @memberof FormStore
     */
    get seenPrice() {
        if (this.itineraryPackages?.every(t => t.totalPriceForCheck)) {
            return this.itineraryPackages.reduce(
                (acc, curr) =>
                    acc +
                    ((curr.needsLoginForDiscount && !useAuthStore().loggedIn
                        ? curr.totalPriceForCheck
                        : curr.totalPayableForCheck) ?? 0),
                0
            );
        }
        return undefined;
    }

    /**
     * GETTER - Returns the trips in the trip package
     *
     * @memberof FormStore
     */
    get trips(): [Trip, TripPriceClass | null][] {
        if (this.itineraryPackages === null) {
            return [];
        }
        return this.itineraryPackages
            .filter<TripPackage>(ItineraryPackage.isTripPackage)
            .flatMap(pkg =>
                pkg.segments
                    .reduce(
                        (acc, segment) => acc.concat(segment.trips),
                        <Trip[]>[]
                    )
                    .map(
                        t =>
                            [t, pkg?.selectedPrice || null] as [
                                Trip,
                                TripPriceClass | null,
                            ]
                    )
            );
    }

    get tripDeparture() {
        if (!this.itineraryPackages) {
            return null;
        }
        const tripPackages = this.itineraryPackages.filter<TripPackage>(
            ItineraryPackage.isTripPackage
        );
        const tripPackage = tripPackages[1] || tripPackages[0];
        if (
            !tripPackages ||
            !tripPackage ||
            !tripPackage.isTripPackage() ||
            !tripPackage.lastTrip ||
            !tripPackage.lastTrip.departureDateTime
        ) {
            return null;
        }
        return tripPackage.lastTrip.departureDateTime;
    }

    /**
     * ACTION - Verifies information of the form
     *
     * @async
     * @memberof FormStore
     */
    async verify() {
        if (
            !this.itineraryPackages ||
            !this.itineraryPackages
                .filter<TripPackage>(ItineraryPackage.isTripPackage)
                .every(p => p.selectedPrice || p.mergedSegment)
        ) {
            return;
        }

        this.verificationStatus = VerificationStatus.Loading;
        const invoiceStore = useInvoiceStore();
        let { itineraryPackages } = this;
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        const currentSearchForm = useCurrentSearchForm();
        const currentModule = useCurrentItineraryModule();
        if (this.emptyPackage) {
            itineraryPackages = [this.emptyPackage];
        }
        invoiceStore.isLoadingEntries = true;
        try {
            let result: VerifyResult | null = null;
            const terms: Map<ItineraryPackage, string> = new Map();
            let billId = null;
            let billCode = null;
            for (const index in itineraryPackages) {
                const itineraryPackage = itineraryPackages[index];
                const response: VerifyResult =
                    await itineraryPackage.createBill(
                        {
                            adultCount: this.adultCount,
                            childCount: this.childCount,
                            infantCount: this.infantCount,
                        },
                        billId
                    );
                if (
                    response.priceEntries[0] &&
                    !response.priceEntries[0].itineraryPackage
                ) {
                    response.priceEntries[0].itineraryPackage =
                        this.itineraryPackages[index];
                }

                response.setFormsPackage(this.itineraryPackages[index]);

                if (response.departureTime) {
                    const seenDepartureTime = (itineraryPackage as TripPackage)
                        .departureDateTime;
                    const actualDepartureTime = response.departureTime;
                    if (
                        seenDepartureTime &&
                        actualDepartureTime &&
                        seenDepartureTime.getTime() !==
                            actualDepartureTime.getTime()
                    ) {
                        this.setActualDepartureTime({
                            index: parseInt(index),
                            time: actualDepartureTime,
                        });
                        response.warningMessages.push({
                            message: `زمان حرکت از ${seenDepartureTime.getHours()}:${seenDepartureTime.getMinutes()} به ${actualDepartureTime.getHours()}:${actualDepartureTime.getMinutes()} تغییر یافته‌‌است.`,
                            type: 'timeChange',
                        });
                    }
                }

                if (!result) {
                    result = response;
                } else {
                    result.merge(response);
                }
                this.reserveNotes = result.reserveNotes;
                ({ billId } = response);
                if (!billCode) {
                    ({ billCode } = response);
                }
                if (response.cancellationTerms) {
                    terms.set(itineraryPackage, response.cancellationTerms);
                }
            }
            if (!result) {
                return;
            }

            // handle submit suggestion services
            const servicesList = itineraryPackages.reduce((prev, current) => {
                if (current.isTripPackage()) {
                    return [
                        ...prev,
                        ...current.selectedSuggestionServices.map(s => ({
                            SuggestedServiceId: s.service.id,
                            Count: s.count,
                            ArrivalTime: current.arrivalDateTime.toISOString(),
                            DepartureTime:
                                current.departureDateTime.toISOString(),
                            Description: s.description,
                        })),
                    ];
                }
                return prev;
            }, [] as AddBillSuggestedServiceRequest[]);
            if (servicesList.length) {
                await BillsApiApiBillsSuggestedServiceBillCodePost({
                    billCode: billCode ?? '',
                    addBillSuggestedServiceRequest: servicesList,
                });
            }

            this.extraInfoFields = result.extraInfoFields;

            if (billCode) {
                this.handleUtmCampaigns(billCode);
            }

            if (result.payablePrice && this.seenPrice) {
                const actualPrice = result.payablePrice;
                if (actualPrice - this.seenPrice > 10000) {
                    currentModule.value.resetLastLoadTime({
                        index: itineraryStore.currentSetIndex,
                    });
                    result.warningMessages.push({
                        message: `مبلغ ${
                            currentSearchForm.value.reserveInfo.title
                        } از ${formatMoney(this.seenPrice)} به ${formatMoney(
                            actualPrice
                        )} افزایش یافته است!`,
                        type: 'priceIncrease',
                    });
                } else if (this.seenPrice - actualPrice > 10000) {
                    currentModule.value.resetLastLoadTime({
                        index: itineraryStore.currentSetIndex,
                    });
                    result.warningMessages.push({
                        message: `خوشبختانه مبلغ ${
                            currentSearchForm.value.reserveInfo.title
                        } از ${formatMoney(this.seenPrice)} به ${formatMoney(
                            actualPrice
                        )} کاهش یافته است!`,
                        type: 'priceDecrease',
                    });
                }
            }

            this.expiryTime = new Date(
                new Date().getTime() + 1000 * result.remainingTime
            );

            invoiceStore.billHandlers = result.billHandlers;

            invoiceStore.setEntries(
                result.priceEntries as ItineraryPackageInvoiceEntries[]
            );
            invoiceStore.isLoadingEntries = false;
            invoiceStore.setBillInfo({
                code: result.billCode || billCode,
                id: result.billId,
                reserveId: result.reserveId,
                mac: result.mac,
            });
            invoiceStore.removeDiscount(result.discountType);
            invoiceStore.addDiscount({
                type: result.discountType,
                price: result.discount,
            });
            this.maxCapacity = result.maxCapacity || 0;
            this.setFormConfigs(result);

            if (result.loadDefaultPassenger) {
                await this.loadDefaultPassenger();
            }
            this.packagesCancellationTerms = terms;
            this.needsDestinationMobile = result.needsDestinationMobile;
            this.verificationStatus = VerificationStatus.Verified;
            return result;
        } catch (ex) {
            if (searchFormStore.reserveType === ReserveType.Hotel) {
                invoiceStore.setPreEntryOfPackage({
                    itineraryPackage: itineraryPackages[0],
                    entries: [],
                });
                usePackageSelectStore().clearSelectedPackages();
            } else {
                for (const index in this.itineraryPackages) {
                    currentModule.value.resetLastLoadTime({
                        index: +index,
                    });
                }
            }
            if (ex instanceof ItineraryVerifyException) {
                this.verificationStatus = ex.error;
            } else {
                this.verificationStatus = VerificationStatus.UnknownError;
            }
            throw ex;
        } finally {
            this.saveSession();
        }
    }

    /**
     * ACTION - Saves the information of current sessions
     *
     * @memberof FormStore
     */
    saveSession() {
        if (this.itineraryPackages === null) {
            return;
        }
        if (this.isLoadFromSession) {
            return;
        }

        const data = <SavedItinerary>{
            itineraryPackages: this.itineraryPackages,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
            emptyPackage: this.emptyPackage,
        };
        sessionStorage.setItem('reserveData', JSON.stringify(data));
    }

    async refetchPrebills(force = false, paxCount?: PassengerCount) {
        const haveDiscount = this.itineraryPackages.some(
            s => s.defaultPrice?.couponCode
        );
        if (this.formDirty || force) {
            const summary = paxCount || this.paxCountSummary;
            let totalDiscount = 0;
            const invoiceStore = useInvoiceStore();
            invoiceStore.isLoadingEntries = true;
            const entries = await Promise.all(
                this.itineraryPackages.map(async p => {
                    const preInvoices = await p.loadPreInvoice({
                        ...summary,
                        loggedIn: useAuthStore().loggedIn,
                    });
                    if (p.defaultPrice?.couponCode) {
                        totalDiscount += preInvoices.reduce(
                            (t, i) => t + (i.discount ?? 0) * i.count,
                            0
                        );
                    }

                    return {
                        itineraryPackage: p,
                        prices: preInvoices.map(p => ({
                            ...p,
                            price: p.price + (p.discount ?? 0),
                        })),
                    };
                })
            );

            invoiceStore.setEntries(entries);
            invoiceStore.isLoadingEntries = false;
            if (haveDiscount) {
                invoiceStore.removeDiscount(DiscountType.FirstPurchase);
                invoiceStore.addDiscount({
                    price: totalDiscount,
                    type: DiscountType.FirstPurchase,
                });
            }
        }
    }

    async refetchPreBillForPassengers(
        formsData: { nationality: Nationality; type: PaxType }[]
    ) {
        const needRefresh = this.itineraryPackages.some(
            pkg => pkg.needRefreshInvoiceOnChangeNationality
        );
        if (needRefresh) {
            const paxCount: PassengerCount = formsData?.reduce(
                (prev, curr) => {
                    if (curr.nationality === Nationality.Foreigner) {
                        return {
                            ...prev,
                            foreignerCount: prev.foreignerCount + 1,
                        };
                    }
                    switch (curr.type) {
                        case PaxType.Adult:
                            return {
                                ...prev,
                                adultCount: prev.adultCount + 1,
                            };
                        case PaxType.Child:
                            return {
                                ...prev,
                                childCount: prev.childCount + 1,
                            };
                        case PaxType.Infant:
                            return {
                                ...prev,
                                infantCount: prev.infantCount + 1,
                            };
                        default:
                            return prev;
                    }
                },
                {
                    infantCount: 0,
                    adultCount: 0,
                    childCount: 0,
                    foreignerCount: 0,
                }
            );
            await this.refetchPrebills(true, paxCount);
        }
    }

    /**
     * ACTION - Loads the last saved session information
     *
     * @memberof FormStore
     */
    loadSession() {
        if (this.itineraryPackages?.length) {
            return;
        }
        const dataStr = sessionStorage.getItem('reserveData');
        if (!dataStr) {
            return;
        }
        this.isLoadFromSession = true;
        const data: SavedItinerary = JSON.parse(dataStr);

        const packages = data.itineraryPackages.map(datum =>
            ItineraryModule.revivePackage(datum)
        );
        this.emptyPackage = data.emptyPackage
            ? ItineraryModule.revivePackage(data.emptyPackage)
            : null;
        this.setItineraryPackagesAndDepartureTime(packages);
        usePackageSelectStore().selectedPackages = packages;
        const searchFormStore = useSearchFormStore();
        searchFormStore.setReserveType(
            packages[0]?.reserveType || this.emptyPackage?.reserveType
        );
        this.setCounts({
            adultCount: data.adultCount,
            childCount: data.childCount,
            infantCount: data.infantCount,
        });
        if (packages[0]?.isHotelPackage()) {
            useHotelForm().setUserDestinationFromPackage(packages[0]);
        }
    }

    /**
     * ACTION - Loads default passenger or the form from the server
     *
     * @async
     * @memberof FormStore
     */
    async loadDefaultPassenger() {
        if (!useAuthStore().loggedIn) {
            return;
        }
        try {
            const { data } =
                await PreviousPaxApiApiPreviousPaxDefaultPassengerGet();
            this.defaultPassenger = {
                firstName: data.FirstName ?? '',
                lastName: data.LastName ?? '',
                persianFirstName: data.PersianFirstName ?? '',
                persianLastName: data.PersianLastName ?? '',
                birthDate: data.BirthDay || null,
                nationalCode: data.NationalCode ?? '',
                passportNumber: data.PassportNumber ?? '',
                passportExpiry: data.PassportExpiration || null,
                gender: data.Male ? Gender.Male : Gender.Female,
                nationality: !data.Nationality
                    ? Nationality.Iranian
                    : Nationality.Foreigner,
                nationalityCode: data.Nationality ?? null,
                title: '',
                type: PaxType.Adult,
                packageOptionalService: [],
                packageFreeService: [],
                carPlate: [],
                carModel: '',
                isDuplicated: false,
            };
        } catch (e) {
            reportException(e);
        }
    }

    paxSort(sortBy: PreviousPaxOrderByType) {
        this.previousPaxOrderBy = sortBy;
    }

    /**
     * ACTION - Loads the information of previous passengers from server
     *
     * @memberof FormStore
     */
    async loadPrevPax(force?: boolean) {
        if (!force && this.prevPaxLoadingStatus !== LoadingStatus.None) {
            return;
        }
        this.prevPaxLoadingStatus = LoadingStatus.Loading;
        try {
            const { data } = await PreviousPaxApiApiPreviousPaxPlayerIdGet({
                playerId: useAuthStore().uuid ?? '',
                orderByType: this.previousPaxOrderBy,
            });

            this.setPrevPax(data);
            this.prevPaxLoadingStatus = LoadingStatus.Success;
        } catch (e) {
            reportException(e);
            this.prevPaxLoadingStatus = LoadingStatus.Error;
        }
    }

    handleUtmCampaigns(billCode: string) {
        this.handleGoogleAnalytics(billCode);
        this.handleRadar361Params(billCode);
        this.handleTakhfifanParams(billCode);
        this.handleSafarMarketParams(billCode);
        this.handleWebengageParams(billCode);
    }

    handleRadar361Params(billCode: string) {
        try {
            const val = localStorage.getItem('utm');
            const list = JSON.parse(val ?? '[]') as Utm[];
            const lastItem = list[list.length - 1];
            if (
                lastItem?.utmSource === 'radar361' &&
                lastItem.utmKey &&
                lastItem.utmServiceType
            ) {
                AnalyticsApiApiAnalyticsRadar361BillCodePost({
                    billCode,
                    radar361BillAnalyticParamRequest: {
                        Key: lastItem.utmKey,
                        ServiceType: lastItem.utmServiceType,
                    },
                });
            }
        } catch (e) {
            reportException(e);
        }
    }

    handleGoogleAnalytics(billCode: string) {
        try {
            const w = window as any;
            const clientId = getCookie('_ga')?.substring(6);
            let sessionId = '';
            try {
                if (typeof w.google_tag_manager !== 'undefined') {
                    const cookieId = Object.keys(w.google_tag_manager)
                        .find(item => item.startsWith('G-'))
                        ?.replace('G-', '_ga_');
                    const regex = new RegExp(
                        `${cookieId}=GS\\d\\.\\d\\.(.+?)(?:;|$)`
                    );
                    const match = RegExp(regex).exec(document.cookie);
                    const parts = match?.[1].split('.');
                    sessionId = parts?.[0] ?? '';
                }
            } catch (e) {
                reportException(e);
            }
            if (clientId || sessionId) {
                AnalyticsApiApiAnalyticsGoogleAnalyticsBillCodePost({
                    billCode,
                    googleBillAnalyticParamRequest: {
                        ClientId: clientId,
                        SessionId: sessionId,
                    },
                });
            }
        } catch (e) {
            reportException(e);
        }
    }

    handleTakhfifanParams(billCode: string) {
        try {
            const val = LocalStorageUtils.getItemOrNull<Utm>('takhfifan');
            if (val?.utmSource === 'takhfifan' && val.utmKey) {
                AnalyticsApiApiAnalyticsTakhfifanBillCodePost({
                    billCode,
                    takhfifanBillAnalyticParamRequest: {
                        Key: val.utmKey,
                    },
                });
            }
        } catch (e) {
            reportException(e);
        }
    }

    handleSafarMarketParams(billCode: string) {
        try {
            const id = useSafarMarket().getSmId();
            if (id) {
                AnalyticsApiApiAnalyticsSafarmarketBillCodePost({
                    billCode,
                    safarMarketBillAnalyticParamRequest: {
                        SafarMarketId: id,
                    },
                });
            }
        } catch (e) {
            reportException(e);
        }
    }

    handleWebengageParams(billCode: string) {
        try {
            window.webengage?.onReady(() => {
                if (
                    window.webengage?.state.getForever &&
                    window.webengage?.state.getForever()?.cuid
                ) {
                    return;
                }
                const id =
                    window.webengage?.user.getAnonymousId &&
                    window.webengage?.user.getAnonymousId();
                if (id) {
                    AnalyticsApiApiAnalyticsWebengageBillCodePost({
                        billCode,
                        webEngageBillAnalyticParamRequest: {
                            UserId: id,
                        },
                    });
                }
            });
        } catch (e) {
            reportException(e);
        }
    }

    get genderLimitation() {
        return this.itineraryPackages.find(pkg => pkg.hasGenderLimitation)
            ?.hasGenderLimitation;
    }
}

export const { use: useFormStore, useIfInstalled: useFormStoreIfInstalled } =
    defineClassStore('form', () => new FormStore());
